import React, { useState, useEffect } from "react";
import { IS_VATAR } from "../vatar/env";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  arrayUnion,
  serverTimestamp,
  getDocs,
  limit,
} from "firebase/firestore";
import { db } from "../firebase"; // Adjust path to your firebase config
import { statsService } from "../Services/statisticsService";
import { systemMessageService } from "../Services/systemMessageService";
import authService from "../Services/authService";
import analytics from "../Services/analyticsService";
import useEnsureDisplayName from "../hooks/useEnsureDisplayName";
import EditNameModal from "../Components/EditNameModal";
import PlayingAsRow from "../Components/PlayingAsRow";
import { uniquePlayerName } from "../utils/uniquePlayerName";
import { ArrowLeft } from "lucide-react";
import FinalGameResults from "../Components/FinalGameResults";

const JoinByCode = () => {
  const [code, setCode] = useState("");
  const navigate = useNavigate();

  const handleJoin = () => {
    const trimmed = code.trim();
    if (trimmed) navigate(`/join-room/${trimmed}`, { state: { joinMethod: "code" } });
  };

  return (
    <div className="min-h-screen bg-orange-50 flex flex-col items-center justify-center px-6">
      <h1 className="text-4xl font-bold text-red-800 mb-2">NameIt!</h1>
      <p className="text-gray-600 mb-8 text-center">Enter your room code to join a game</p>
      <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-sm">
        <label className="block text-sm font-medium text-gray-700 mb-2">Room Code</label>
        <input
          type="text"
          className="border border-red-200 py-3 px-4 text-center w-full mb-4 rounded-lg text-lg tracking-widest uppercase focus:outline-none focus:ring-2 focus:ring-red-400"
          placeholder="e.g. ABC234"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleJoin()}
          autoFocus
        />
        <button
          onClick={handleJoin}
          disabled={!code.trim()}
          className={`w-full py-3 rounded-lg font-semibold text-white transition ${
            code.trim() ? "bg-red-600 hover:bg-red-700" : "bg-gray-300 cursor-not-allowed"
          }`}
        >
          Join Room
        </button>
        <button
          onClick={() => navigate("/")}
          className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700 underline"
        >
          Create a new room instead
        </button>
      </div>
    </div>
  );
};

const JoinRoom = () => {
  const { roomId } = useParams();
  const location = useLocation();
  // Set by JoinByCode's navigate() when a room code was typed in-app;
  // absent when a player lands here directly from a shared URL.
  const joinMethod = location.state?.joinMethod || "link";
  const {
    displayName, playerName, draftName, setDraftName, showEditName, setShowEditName, ensureName, syncToAccount,
  } = useEnsureDisplayName();
  const [roomData, setRoomData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [showRecoveryForm, setShowRecoveryForm] = useState(false);
  const [recoveryName, setRecoveryName] = useState("");
  const [recoveryError, setRecoveryError] = useState("");
  const currentRoomId = localStorage.getItem("currentRoomId");
  const isHost = localStorage.getItem("isHost") === "true";

  useEffect(() => {
    if (currentRoomId === roomId && isHost) {
      // User is trying to join their own hosted room
      navigate("/create-room"); // Redirect to host view
      return;
    }

    // fetchRoomData is async (the short-code lookup awaits a getDocs before
    // ever reaching the onSnapshot call) — if roomId changes again or this
    // component unmounts while that lookup is still pending, its eventual
    // onSnapshot listener must never be installed (or must be torn down
    // immediately), or it keeps firing for a room this screen has already
    // moved on from and can overwrite state with stale data. `isStale`
    // covers the state-update side; `unsubscribeRef` lets this effect's own
    // cleanup dispose of whatever listener the async call ends up creating,
    // even though it doesn't exist yet at the time cleanup runs.
    let isStale = false;
    const unsubscribeRef = { current: null };

    fetchRoomData(() => isStale, unsubscribeRef);

    return () => {
      isStale = true;
      if (unsubscribeRef.current) unsubscribeRef.current();
    };
  }, [roomId]);

  useEffect(() => {
    if (roomData && !isHost) {
      const storedHostId = localStorage.getItem("hostPlayerId");
      const currentHostId = roomData.hostId; // or however you store host ID in room data

      if (storedHostId && storedHostId === currentHostId) {
        // Automatically reclaim host status
        reclaimHostStatus();
      }
    }
  }, [roomData, isHost]);

  // Function to reclaim host status
  const reclaimHostStatus = async () => {
    try {
      // API call to update host status in DB
      await updateHostStatus(roomId, true);
      // setIsHost(true);
      // Show success message if needed
    } catch (error) {
      console.error("Failed to reclaim host status:", error);
    }
  };

  const fetchRoomData = async (isStale, unsubscribeRef) => {
    // This route also receives short join codes now (see
    // roomCreationService.js's generateShortCode / CreateRoom.jsx's share
    // link) — unlike joinRoomService.js's findRoom, this screen queried only
    // the long internal roomId field, so a shared short-code link or a code
    // typed into JoinByCode always came back "Room not found". Try the short
    // code first, same as findRoom, before falling back to the roomId query.
    try {
      const shortCodeSnapshot = await getDocs(
        query(
          collection(db, "rooms"),
          where("shortCode", "==", (roomId || "").toUpperCase()),
          where("isActive", "==", true),
          limit(1)
        )
      );

      // The effect that called us may have moved on (roomId changed again,
      // or the component unmounted) while that getDocs was in flight —
      // don't install a listener for a lookup nothing is listening for
      // the result of anymore.
      if (isStale()) return;

      if (!shortCodeSnapshot.empty) {
        const docId = shortCodeSnapshot.docs[0].id;
        const unsubscribe = onSnapshot(doc(db, "rooms", docId), (docSnap) => {
          if (isStale()) return;
          if (docSnap.exists()) {
            setRoomData({ id: docSnap.id, ...docSnap.data() });
            setError("");
          } else {
            setError("Room not found. Please check the link and try again.");
          }
          setLoading(false);
        });
        unsubscribeRef.current = unsubscribe;
        return;
      }
    } catch (err) {
      console.warn("Short code lookup failed, falling back to roomId query:", err);
      if (isStale()) return;
    }

    const q = query(collection(db, "rooms"), where("roomId", "==", roomId));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      if (isStale()) return;
      if (!querySnapshot.empty) {
        const docSnap = querySnapshot.docs[0];
        const data = { id: docSnap.id, ...docSnap.data() };
        setRoomData(data);
        setError("");
      } else {
        setError("Room not found. Please check the link and try again.");
      }
      setLoading(false);
    });
    unsubscribeRef.current = unsubscribe;
  };

  const generateUniqueId = () => {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  };

  const joinRoom = async () => {
    if (!playerName.trim()) {
      setError("Please enter your name");
      return;
    }

    if (!roomData) {
      analytics.track("room_join_failed", {
        reason: "room_not_found",
        join_method: joinMethod,
      });
      setError("Room not found");
      return;
    }

    // Check if room is full
    const currentPlayerCount = roomData.players ? roomData.players.length : 0;
    const maxPlayers = roomData.maxPlayers || 3;

    if (currentPlayerCount >= maxPlayers) {
      analytics.track("room_join_failed", {
        reason: "room_full",
        join_method: joinMethod,
      });
      setError("Room is full. Cannot join.");
      return;
    }

    // Two players can't share a name in a room, but nobody is turned away
    // for it: the second Semi joins as "Semi 2".
    const finalName = uniquePlayerName(playerName, roomData.players);

    try {
      await ensureName();
    } catch (err) {
      setError(err.message);
      return;
    }

    setJoining(true);
    setError("");

    try {
      // Get Firebase anonymous user ID
      const userId = await authService.getCurrentUserId();

      const newPlayer = {
        id: userId, // Now using Firebase UID
        name: finalName,
        isHost: false,
        joinedAt: new Date(),
      };

      // Update the room document
      const roomDocRef = doc(db, "rooms", roomData.id);
      await updateDoc(roomDocRef, {
        players: arrayUnion(newPlayer),
      });

      if (roomData.id) {
        try {
          await systemMessageService.playerJoined(
            roomData.id,
            finalName
          );
        } catch (error) {
          console.warn("Failed to send join message:", error);
        }
      }

      // Create user document and add room — use the room's own canonical
      // roomId (roomData.roomId), not the raw URL param, which may be a
      // short join code. Every other read (recent rooms, "already in room"
      // checks) keys off that canonical field, so storing the short code
      // here would make this room silently unfindable afterwards.
      const canonicalRoomId = roomData.roomId;
      await authService.createUserDocument(userId);
      await authService.addRoomToUser(canonicalRoomId, {
        roomName: roomData.roomName,
        isHost: false,
      });

      // Store in localStorage (keep for compatibility)
      localStorage.setItem("currentRoomId", canonicalRoomId);
      localStorage.setItem("currentUserId", userId); // Now Firebase UID
      localStorage.setItem("playerName", finalName);
      localStorage.setItem("isHost", "false");

      const existingJoinedRooms = JSON.parse(
        localStorage.getItem("joinedRooms") || "[]"
      );
      const updatedJoinedRooms = [
        ...existingJoinedRooms,
        {
          roomId: canonicalRoomId,
          roomName: roomData.roomName,
          joinedAt: Date.now(),
          joinedAs: finalName,
        },
      ];

      if (updatedJoinedRooms.length > 5) {
        updatedJoinedRooms.splice(0, updatedJoinedRooms.length - 5);
      }

      localStorage.setItem("joinedRooms", JSON.stringify(updatedJoinedRooms));

      analytics.track("room_joined", {
        room_id: canonicalRoomId,
        join_method: joinMethod,
        player_count_at_join: currentPlayerCount,
      });

      syncToAccount();
      navigate("/create-room");
    } catch (error) {
      console.error("Error joining room:", error);
      analytics.track("room_join_failed", {
        reason: error.code || error.message || "unknown",
        join_method: joinMethod,
      });
      setError("Failed to join room. Please try again.");
    } finally {
      setJoining(false);
    }
  };

  const handleInputChange = (e) => {
    setDraftName(e.target.value);
    if (error && e.target.value.trim()) {
      setError("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      joinRoom();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-orange-50 container px-6 flex justify-center items-center">
        <div className="text-red-800 text-xl">Loading room...</div>
      </div>
    );
  }

  // Show final results if game has ended
  if (roomData && roomData.gameStatus === "ended") {
    return (
      <div className="min-h-screen bg-orange-50 container px-6 py-8">
        <h1 className="text-4xl text-center py-4 font-bold text-red-800">
          NameIt!
        </h1>
        <FinalGameResults roomData={roomData} roomDocId={roomData.roomId} currentUserId={localStorage.getItem("currentUserId")} />
      </div>
    );
  }

  // Show game in progress message if game is active
  if (
    roomData &&
    (roomData.gameStatus === "playing" || roomData.gameStatus === "starting")
  ) {
    return (
      <div className="min-h-screen bg-orange-50 container px-6">
        <h1 className="text-4xl text-center py-4 font-bold text-red-800">
          NameIt!
        </h1>
        <div className="max-w-md mx-auto text-center py-8">
          <h2 className="text-2xl font-semibold mb-4">Game in Progress</h2>
          <p className="text-gray-600 mb-4">
            This game has already started. You cannot join a game that's in
            progress.
          </p>
          {/* <p className="text-sm text-gray-500 mb-6">
            Game Status:{" "}
            {roomData.gameStatus === "starting" ? "Starting Soon" : "Playing"}
          </p> */}
          <button
            onClick={() => navigate("/")}
            className="bg-red-600 text-white font-semibold px-6 py-2 rounded-lg hover:bg-red-700 transition duration-300"
          >
            Start New Game
          </button>
          <p className="text-gray-500 text-sm mt-4">
            Already in this room?{" "}
            <button
              onClick={() => navigate("/create-room")}
              className="text-red-600 underline hover:text-red-700"
            >
              Rejoin Here
            </button>
          </p>
        </div>
      </div>
    );
  }

  // "Room not found" has its own panel below; don't repeat it in the error box.
  const roomMissing = !roomData;
  // Only offer "Rejoin" to someone this browser actually put in this room.
  const wasInThisRoom = !!roomData && currentRoomId === roomData.roomId;

  return (
    <div className="min-h-screen bg-orange-50 container px-6">
      <div className="max-w-md mx-auto relative">
        <button
          type="button"
          onClick={() => navigate("/")}
          aria-label="Back to home"
          className="absolute left-0 top-3 w-11 h-11 -ml-2 flex items-center justify-center rounded-full text-red-800 hover:bg-red-100 transition-colors"
        >
          <ArrowLeft size={22} />
        </button>
        <h1 className="text-4xl text-center py-4 font-bold text-red-800">
          NameIt!
        </h1>
      </div>

      <div className="max-w-md mx-auto">
        {error && !roomMissing && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {roomData ? (
          <>
            <h2 className="text-xl text-center mb-4 font-semibold">
              Join {roomData.roomName}
            </h2>
            {!displayName && (
              <p className="text-center text-gray-600 mb-4">
                Enter your name to join the game
              </p>
            )}
            <p className="text-center text-sm text-gray-500 mb-6">
              Players: {roomData.players ? roomData.players.length : 0}/
              {roomData.maxPlayers || 3}
            </p>

            {displayName ? (
              <PlayingAsRow
                name={displayName}
                onChange={() => setShowEditName(true)}
                className="mb-4 bg-white border border-red-100"
              />
            ) : (
              <input
                type="text"
                aria-label="Your name"
                className="border border-red-100 py-2 px-4 text-center w-full mb-4"
                placeholder="Enter your name to join"
                value={draftName}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                disabled={joining}
              />
            )}

            <div className="flex justify-center">
              <button
                onClick={joinRoom}
                disabled={joining || !playerName.trim()}
                className={`bg-red-600 text-white font-semibold px-6 py-2 rounded-lg hover:bg-red-700 transition duration-300 ${
                  joining || !playerName.trim()
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {joining ? "Joining..." : "Join Room"}
              </button>
            </div>

            {/* {!isHost && roomData && (
              <div className="mt-8 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowRecoveryForm(!showRecoveryForm)}
                  className="text-red-600 text-sm hover:underline"
                >
                  I am the host of this room
                </button>

                {showRecoveryForm && (
                  <div className="mt-4 p-4 bg-red-50 rounded-lg">
                    <h3 className="text-md font-semibold mb-2">
                      Recover Host Access
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Enter your host name to recover access to host controls:
                    </p>

                    {recoveryError && (
                      <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded mb-3 text-sm">
                        {recoveryError}
                      </div>
                    )}

                    <input
                      type="text"
                      className="border border-red-200 rounded px-3 py-2 w-full mb-3"
                      placeholder="Enter your host name"
                      value={recoveryName}
                      onChange={(e) => setRecoveryName(e.target.value)}
                    />

                    <button
                      onClick={attemptHostRecovery}
                      className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                    >
                      Recover Host Access
                    </button>
                  </div>
                )}
              </div>
            )} */}
            {roomData.players && roomData.players.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg mb-2">Current Players:</h3>
                {roomData.players.map((player, index) => (
                  <div
                    key={player.id}
                    className="bg-red-50 py-2 px-2 border border-orange-300 rounded-md mt-2"
                  >
                    <p>
                      {index + 1}. {player.name}
                      {player.isHost && (
                        <span className="text-red-600 font-semibold">
                          {" "}
                          - host
                        </span>
                      )}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-center">
            <p className="text-red-600 text-lg mb-4">Room not found</p>
            <p className="text-gray-600 mb-4">
              The room link might be invalid or the room might have been closed.
            </p>
            <button
              onClick={() => navigate("/")}
              className="bg-red-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-red-700 transition duration-300"
            >
              Go Home
            </button>
          </div>
        )}

        <div className="text-center mt-6">
          {wasInThisRoom && (
            <p className="text-gray-600 text-sm">
              Already in this room?{" "}
              <button
                onClick={() => navigate("/create-room")}
                className="text-red-600 underline hover:text-red-700"
              >
                Rejoin Here
              </button>
            </p>
          )}
          {/* <p className="text-gray-500 text-sm">
            Don't have a room link?{" "}
            <button
              onClick={() => navigate("/")}
              className="text-red-600 underline hover:text-red-700"
            >
              Create your own room
            </button>
          </p> */}
        </div>
      </div>

      <EditNameModal
        isOpen={showEditName}
        onClose={() => setShowEditName(false)}
        onSaved={() => setError("")}
      />
    </div>
  );
};

export { JoinByCode };
export default JoinRoom;
